'use client'

import { Money24, Percent24, Refund24, X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Icon, Navigation } from '@vinted/web-ui'

import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { ComplaintResolutionChoice } from '@marketplace-web/complaints/complaints-data'

import { partialRefundOffersClickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'

const iconNameMap = {
  [ComplaintResolutionChoice.OfferPartialRefund]: Percent24,
  [ComplaintResolutionChoice.RequestToReturn]: Refund24,
  [ComplaintResolutionChoice.Refund]: Money24,
}

type Props = {
  isOpen: boolean
  complaintId?: string | null
  transactionId?: string
  complaintResolutionActions?: Array<ComplaintResolutionChoice> | null
  isComplaintLoading: boolean
  onDialogOpen: (resolution: ComplaintResolutionChoice) => void
  onClose: () => void
}

const ChooseComplaintResolutionDialog = ({
  isOpen,
  complaintResolutionActions,
  transactionId,
  complaintId,
  isComplaintLoading,
  onDialogOpen,
  onClose,
}: Props) => {
  const translate = useTranslate('choose_complaint_resolution_dialog')

  const { track } = useTracking()

  const trackResolutionClick = (resolution: ComplaintResolutionChoice) => {
    if (!transactionId || !complaintId) return

    const targetDetails = JSON.stringify({
      resolution_type: resolution,
      available_resolutions: complaintResolutionActions,
    })

    track(
      partialRefundOffersClickEvent({
        target: ClickableElement.ComplaintResolution,
        transactionId,
        complaintId,
        targetDetails,
      }),
    )
  }

  const handleResolutionClick = (resolution: ComplaintResolutionChoice) => {
    onDialogOpen(resolution)
    trackResolutionClick(resolution)
  }

  const renderNavigation = () => (
    <>
      <Navigation
        body={translate('title')}
        right={
          <Button
            inline
            iconName={X24}
            styling={Button.Styling.Flat}
            onClick={onClose}
            aria={{ 'aria-label': translate('a11y.close') }}
          />
        }
      />
      <Divider />
    </>
  )

  const renderIcon = (resolution: ComplaintResolutionChoice) => {
    const iconName = iconNameMap[resolution]

    return iconName ? <Icon name={iconName} color={Icon.Color.Primary} /> : null
  }

  const renderResolutionAction = (resolution: ComplaintResolutionChoice) => {
    const title = translate(`actions.${resolution}.title`, { resolution })
    const body = translate(`actions.${resolution}.body`, { resolution })

    return (
      <Cell
        key={resolution}
        prefix={renderIcon(resolution)}
        title={title}
        body={body}
        type={Cell.Type.Navigating}
        onClick={() => handleResolutionClick(resolution)}
        chevron
        divider
        testId={`resolution-choice-${resolution}`}
      />
    )
  }

  const renderResolutionActions = () => {
    if (isComplaintLoading) return <ContentLoader />

    if (!complaintResolutionActions) return null

    return complaintResolutionActions.map(renderResolutionAction)
  }

  return (
    <Dialog show={isOpen}>
      {renderNavigation()}
      {renderResolutionActions()}
    </Dialog>
  )
}

export default ChooseComplaintResolutionDialog
